// @flow

import { isLastElementInArray } from '../../common-utils/utils';
import {
  ADVANCED_SECURITY_TIERS_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_CONFIG_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_END_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_NAME_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_START_BACKEND_NAME,
  ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME,
} from '../../constants';

import {
  ADVANCED_SECURITY_DEFAULT_TIER_1_END_VALUE,
  ADVANCED_SECURITY_DEFAULT_TIER_2_END_VALUE,
  ADVANCED_SECURITY_DEFAULT_USAGE_PERCENTAGE_VALUE,
  ADVANCED_SECURITY_TIERS_PATH,
} from './constants';

// Formik path to a single tier's fields, e.g. advanced_security_custom_discounts_json.tiers.0
export const getPathToTier = (tierIndex) => [...ADVANCED_SECURITY_TIERS_PATH, String(tierIndex)];

// Returns the default form value for advanced security add-on
// Tier 1 and Tier 2 get default end boundaries; the last tier has no end (extends to Infinity).
export const getDefaultAdvancedSecurityTiers = () => [
  {
    [ADVANCED_SECURITY_TIER_END_BACKEND_NAME]: ADVANCED_SECURITY_DEFAULT_TIER_1_END_VALUE,
    [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]:
      ADVANCED_SECURITY_DEFAULT_USAGE_PERCENTAGE_VALUE,
  },
  {
    [ADVANCED_SECURITY_TIER_END_BACKEND_NAME]: ADVANCED_SECURITY_DEFAULT_TIER_2_END_VALUE,
    [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]:
      ADVANCED_SECURITY_DEFAULT_USAGE_PERCENTAGE_VALUE,
  },
  // Tier 3 has no tier_end — it always extends to Infinity, which is not stored.
  {
    [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]:
      ADVANCED_SECURITY_DEFAULT_USAGE_PERCENTAGE_VALUE,
  },
];

// Tier N's start is Tier N-1's end. Tier 1 always starts at 0.
export const getTierStart = (tiers, index) => {
  if (index === 0) {
    return 0;
  }
  const previousTier = tiers[index - 1];
  return Number(previousTier[ADVANCED_SECURITY_TIER_END_BACKEND_NAME]);
};

/**
 * @param {?Object} response - The config object `{ tier_config: [{ tier, start, end, usage_percentage }] }`, or null.
 * @returns {Object} The Formik value: `{}` when unconfigured, else `{ tiers: [{ end?, usage_percentage }] }`.
 * @example
 * transformAdvancedSecurityConfigurationResponseToFormValue(null); // => {}
 * @example
 * transformAdvancedSecurityConfigurationResponseToFormValue({
 *   tier_config: [
 *     { tier: 'tier1', start: 0, end: 250000, usage_percentage: 10 },
 *     { tier: 'tier2', start: 250000, end: 500000, usage_percentage: 10 },
 *     { tier: 'tier3', start: 500000, end: null, usage_percentage: 10 },
 *   ],
 * });
 * // => { tiers: [{ end: 250000, usage_percentage: 10 },
 * //              { end: 500000, usage_percentage: 10 },
 * //              { usage_percentage: 10 }] }
 */
export const transformAdvancedSecurityConfigurationResponseToFormValue = (response) => {
  if (response == null) {
    return {};
  }
  const tiers = response[ADVANCED_SECURITY_TIER_CONFIG_BACKEND_NAME].map((tier) => {
    const usagePercentage = tier[ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME];
    const end = tier[ADVANCED_SECURITY_TIER_END_BACKEND_NAME];
    if (end == null) {
      return { [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]: usagePercentage };
    }
    return {
      [ADVANCED_SECURITY_TIER_END_BACKEND_NAME]: end,
      [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]: usagePercentage,
    };
  });
  return { [ADVANCED_SECURITY_TIERS_BACKEND_NAME]: tiers };
};

/**
 * @param {Array<Object>} tiers - The form tiers, e.g. `[{ end, usage_percentage }, ..., { usage_percentage }]`.
 * @returns {Array<Object>} The tier_config payload: `[{ tier, start, end, usage_percentage }]`.
 * @example
 * buildAdvancedSecurityTierConfigPayload([
 *   { end: '250000', usage_percentage: '10' },
 *   { end: '500000', usage_percentage: '10' },
 *   { usage_percentage: '10' },
 * ]);
 * // => [
 * //   { tier: 'tier1', start: 0, end: 250000, usage_percentage: 10 },
 * //   { tier: 'tier2', start: 250000, end: 500000, usage_percentage: 10 },
 * //   { tier: 'tier3', start: 500000, end: null, usage_percentage: 10 },
 * // ]
 */
export const buildAdvancedSecurityTierConfigPayload = (tiers) =>
  tiers.map((tier, index) => ({
    [ADVANCED_SECURITY_TIER_NAME_BACKEND_NAME]: `tier${index + 1}`,
    [ADVANCED_SECURITY_TIER_START_BACKEND_NAME]: getTierStart(tiers, index),
    [ADVANCED_SECURITY_TIER_END_BACKEND_NAME]: isLastElementInArray(tiers, index)
      ? null
      : Number(tier[ADVANCED_SECURITY_TIER_END_BACKEND_NAME]),
    [ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]: Number(
      tier[ADVANCED_SECURITY_USAGE_PERCENTAGE_BACKEND_NAME]
    ),
  }));
